import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faDownload, faSortAmountDown, faSort, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { calculateBalances, calculateSponsorAmounts, getShareRatio } from '../../utils/helpers';
import { exportBalancesToCSV } from '../../utils/exportHelpers';
import { useEventUsersCache } from '../../hooks/useEventUsersCache';
import { Expense, ShareRatios, SponsorExpense } from '../../types';
import EmptyState from './EmptyState';
import IndividualBalanceCard from './IndividualBalanceCard';
import UserInfoModal from '../event/UserInfoModal';
import { useTranslation } from 'react-i18next';


type SortMode = 'default' | 'balanceHighToLow' | 'balanceLowToHigh' | 'byName';

interface IndividualBalancesTabProps {
    expenses: (Expense | SponsorExpense)[];
    participants: Record<string, string>;
    eventId: string;
    eventName: string;
    shareRatios?: ShareRatios;
}

const IndividualBalancesTab: React.FC<IndividualBalancesTabProps> = ({
                                                                         expenses,
                                                                         participants,
                                                                         eventId,
                                                                         eventName,
                                                                         shareRatios
                                                                     }) => {
    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [sortMode, setSortMode] = useState<SortMode>('default');
    const { t } = useTranslation();


    const { eventUsers, loading: loadingNames } = useEventUsersCache(eventId);

    const selectedUserProfile = useMemo(() => {
        if (!selectedUserId || !showUserModal) return null;
        return eventUsers[selectedUserId] || null;
    }, [selectedUserId, showUserModal, eventUsers]);

    const participantIds = useMemo(() => Object.keys(participants), [participants]);

    const balances = useMemo(
        () => calculateBalances(expenses, participantIds, shareRatios),
        [expenses, participantIds, shareRatios]
    );
    const sponsorAmounts = useMemo(
        () => calculateSponsorAmounts(expenses, participantIds),
        [expenses, participantIds]
    );

    // Sorted participant list
    const sortedParticipantIds = useMemo(() => {
        const ids = [...participantIds];
        switch (sortMode) {
            case 'balanceHighToLow':
                return ids.sort((a, b) => {
                    const balA = balances[a] - (sponsorAmounts[a] || 0);
                    const balB = balances[b] - (sponsorAmounts[b] || 0);
                    return balB - balA;
                });
            case 'balanceLowToHigh':
                return ids.sort((a, b) => {
                    const balA = balances[a] - (sponsorAmounts[a] || 0);
                    const balB = balances[b] - (sponsorAmounts[b] || 0);
                    return balA - balB;
                });
            case 'byName':
                return ids.sort((a, b) => {
                    const nameA = (participants[a] || a).toLowerCase();
                    const nameB = (participants[b] || b).toLowerCase();
                    return nameA.localeCompare(nameB);
                });
            default:
                return ids;
        }
    }, [participantIds, sortMode, balances, sponsorAmounts, participants]);

    const handleViewProfile = (participantId: string) => {
        setSelectedUserId(participantId);
        setShowUserModal(true);
    };

    const handleCloseModal = () => {
        setShowUserModal(false);
        setSelectedUserId(null);
    };

    const handleExportBalances = () => {
        exportBalancesToCSV(balances, participants, eventName, t);
    };

    // Early returns must stay below every hook (Rules of Hooks)
    if (loadingNames) {
        return (
            <div className="glass-card p-8 flex items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                {t('event.balances.loading')}
            </div>
        );
    }

    if (expenses.length === 0) {
        return <EmptyState />;
    }

    const sortOptions: { key: SortMode; label: string }[] = [
        { key: 'default', label: t('event.balances.sorting.default') },
        { key: 'balanceHighToLow', label: t('event.balances.sorting.balanceHighToLow') },
        { key: 'balanceLowToHigh', label: t('event.balances.sorting.balanceLowToHigh') },
        { key: 'byName', label: t('event.balances.sorting.byName') },
    ];

    return (
        <>
            <div className="glass-card overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mr-3">
                                <FontAwesomeIcon icon={faCoins} className="text-blue-600 dark:text-blue-400"/>
                            </div>
                            <div>
                                <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                                    {t('event.balances.title')}
                                </h4>
                            </div>
                        </div>
                        <button
                            onClick={handleExportBalances}
                            className="glass-btn-primary flex items-center !px-3 !py-1.5 text-sm"
                            title={t('event.export.exportBalances')}
                            aria-label={t('event.export.exportBalances')}
                        >
                            <FontAwesomeIcon icon={faDownload} className="mr-2" aria-hidden="true"/>
                            <span className="hidden sm:inline">{t('event.export.exportBalances')}</span>
                            <span className="sm:hidden" aria-hidden="true">CSV</span>
                        </button>
                    </div>
                    {/* Sorting */}
                    <div className="flex items-center gap-1.5 mt-2 overflow-x-auto scrollbar-hide">
                        <FontAwesomeIcon icon={sortMode !== 'default' ? faSortAmountDown : faSort} className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        {sortOptions.map(opt => (
                            <button
                                key={opt.key}
                                onClick={() => setSortMode(opt.key)}
                                className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-all duration-200 ${
                                    sortMode === opt.key
                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Balance Cards */}
                <div className="p-4 pt-1 space-y-2">
                    <div className="grid gap-2">
                        {sortedParticipantIds.map(participantId => (
                            <IndividualBalanceCard
                                key={participantId}
                                participantId={participantId}
                                participantName={participants[participantId] || participantId}
                                balance={balances[participantId]}
                                sponsorAmount={sponsorAmounts[participantId]}
                                shareRatio={getShareRatio(shareRatios, participantId)}
                                onViewProfile={handleViewProfile}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* User Info Modal */}
            {selectedUserId && (
                <UserInfoModal
                    isOpen={showUserModal}
                    onClose={handleCloseModal}
                    userProfile={selectedUserProfile}
                    participantName={participants[selectedUserId] || selectedUserId}
                />
            )}
        </>
    );
};

export default IndividualBalancesTab;